import moment from 'moment'
import { decodeJwtClaims } from '../jwt'

// Shared by the owner's delete page, the restore page, the banner and the
// organization picker (delete organization, plan Tasks 26-27).

// Where an owner's restricted restore session lives (spec §2.5). The
// delete page signs the owner out to it; every path of a restricted session
// is routed to it.
export const SCHEDULED_PATH = '/org/organization/scheduled'

// A deletion date as a day ("1 Nov 2026"), in UTC like the emails and the
// system recycle bin.
export const deletionDay = value => (value ? moment.utc(value).format('D MMM YYYY') : '')

// Scheduled-org member experience (D2, D5): every member sees a scheduled
// organization in the switcher until it is purged; only a row the server
// marks canRestore (its owner, on the admin client, not impersonated) opens
// the restore session. An auth from before 2026-10-04 sent no canRestore and
// listed owners' rows only (TEMPORARY-COMPAT).
export const restorable = organization => !!(organization && organization.scheduledForDeletion && organization.canRestore !== false)

// "A", "A and B", "A, B and C"; "" for none.
export const joinNames = names => {
  const list = (names || []).filter(Boolean)
  if (list.length <= 1) return list[0] || ''
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}

// Whom to contact about a scheduled organization: its owners by full name
// (never an address, D2) -- "its owner, A." for one, "one of its owners: A
// and B." for more -- else EducationXR support.
export const contactLine = names => {
  const list = (names || []).filter(Boolean)
  if (list.length === 1) return `If you think this is a mistake, contact its owner, ${list[0]}.`
  if (list.length > 1) return `If you think this is a mistake, contact one of its owners: ${joinNames(list)}.`
  return 'If you think this is a mistake, contact EducationXR support.'
}

// The restricted restore session's claim.
export const isRestrictedSession = claims => !!(claims && claims.orgPendingDeletion)

// The session's claims: the auth context's once the first refresh has filled
// them, else the stored token's (after a reload the context's are {} until
// then).
export const currentClaims = state => {
  const filled = state && state.jwtClaims && Object.keys(state.jwtClaims).length ? state.jwtClaims : null
  return filled || decodeJwtClaims(state && state.accessToken) || {}
}

// The auth service's refusal of a member's or admin's sign-in to an
// organization scheduled for deletion (Task 25), and what the sign-in page
// says (spec §2.3).
export const SCHEDULED_REASON = 'organization_scheduled_for_deletion'
export const SCHEDULED_SIGN_IN_MESSAGE = 'This organization is scheduled for deletion. Contact an owner of the organization.'

// After a restore, every service's organization gate catches up within its
// refresh interval (core orggate DefaultRefresh, 10 s). Until then a request
// may still meet 401 organization_unavailable; that must not end the
// session. The restore page records when it restored; the global 401
// handler reads it.
export const RESTORE_GRACE_MS = 60 * 1000
const RESTORED_AT_KEY = 'edxr-org-restored-at'

export const markRestored = (now = Date.now()) => {
  try { sessionStorage.setItem(RESTORED_AT_KEY, String(now)) } catch (e) {}
  // The banner's list is stale now.
  forgetPending()
}

export const restoredAt = () => {
  try {
    const at = Number(sessionStorage.getItem(RESTORED_AT_KEY))
    return at > 0 ? at : null
  } catch (e) { return null }
}

// The banner's GET organization-deletion/pending, read once per session (the
// login): a remount -- the full layout comes back after the restore page,
// a reload of the shell -- reuses it. A restore forgets it.
const pendingCache = { key: null, promise: null }

export const forgetPending = () => {
  pendingCache.key = null
  pendingCache.promise = null
}

export const readPendingOnce = (key, fetch) => {
  if (pendingCache.key !== key || !pendingCache.promise) {
    pendingCache.key = key
    pendingCache.promise = Promise.resolve()
      .then(fetch)
      .then(items => (Array.isArray(items) ? items : []))
      .catch(() => [])
  }
  return pendingCache.promise
}

// Finishing a restore (or Continue when another owner restored it): every
// service's organization gate follows within its refresh interval (core
// orggate, 10 s), and auth is the one that lagged when tried. So: record
// the restore (the 401 grace), take the refreshed token (it no longer
// carries orgPendingDeletion) WITHOUT adopting it yet, and probe a gated
// route with it, backing off, until it gets through; only then adopt it and
// land. Never ready within about 30 s: false, and the page says it is
// finishing (try again shortly). While the token is adopted and the landing
// runs, isLanding() is true, so the restore page's route in the full layout
// shows a spinner and makes no request of its own.
export const RESTORE_PROBE_DELAYS = [1000, 2000, 4000, 8000, 15000]

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

let landing = false
export const isLanding = () => landing

export const finishRestore = async ({ refreshToken, fetchToken, probe, adopt, land, delays = RESTORE_PROBE_DELAYS, sleep = wait, fallback = () => window.location.assign('/') }) => {
  markRestored()

  let authToken
  try {
    authToken = await fetchToken(refreshToken)
  } catch {
    return false
  }
  if (!authToken || !authToken.accessToken) return false

  let ready = false
  for (let i = 0; ; i++) {
    try {
      await probe(authToken)
      ready = true
      break
    } catch {}
    if (i >= delays.length) break
    await sleep(delays[i])
  }
  if (!ready) return false

  landing = true
  try {
    adopt(authToken)
    try {
      await land()
    } catch {
      // The token is adopted and the restore page is gone: start afresh.
      fallback()
    }
  } finally {
    landing = false
  }
  return true
}
